import { Link } from "react-router-dom";
import { ArrowUpRight, Clock } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { LiquidPageHeader } from "@/components/liquid/LiquidPageHeader";
import { ZoomParallax } from "@/components/liquid/ZoomParallax";
import { VelocityMarquee } from "@/components/liquid/VelocityMarquee";
import { VideoBackdrop } from "@/components/liquid/VideoBackdrop";
import { pageVideo } from "@/lib/videos";
import { useContent } from "@/context/ContentContext";
import { useBooking } from "@/context/BookingContext";

export default function BlogLiquid() {
  const { content } = useContent();
  const { book } = useBooking();
  const articles = content?.articles || [];
  const [first, ...rest] = articles;

  return (
    <div className="relative z-[1] px-4 pb-24 pt-36 sm:px-8 sm:pt-44 lg:px-16" data-testid="blog-liquid">
      <VideoBackdrop src={pageVideo(content?.settings, "blog")} testid="blog-video-bg" />
      <div className="mx-auto max-w-6xl">
        <LiquidPageHeader
          index="05"
          eyebrow="The Skin Journal"
          title="Doctor's Notes"
          subtitle="Evidence-based skin, hair and wellness guidance — written by Dr. Javeria Atif, not a marketing team. A new essay every week."
          testid="blog-title"
        />

        {first && (
          <Reveal className="mt-14">
            <Link to={`/blog/${first.id}`} data-testid={`blog-featured-${first.id}`} className="group grid items-center gap-10 lg:grid-cols-12">
              <div className="lg:col-span-7">
                <ZoomParallax src={first.image} alt={first.title} className="h-[50vh] rounded-[4px] shadow-[0_40px_90px_rgba(26,26,26,0.12)]" />
              </div>
              <div className="lg:col-span-5">
                <div className="flex items-center gap-4 text-[11px] font-medium uppercase tracking-[0.25em] text-[#B38B2D]">
                  <span>{first.category}</span>
                  <span className="flex items-center gap-1.5 text-[#1A1A1A]/40"><Clock className="h-3.5 w-3.5" aria-hidden="true" />{first.read_time}</span>
                </div>
                <h2 className="mt-5 font-display text-4xl font-normal leading-tight text-[#1A1A1A] sm:text-5xl">{first.title}</h2>
                <p className="mt-5 text-sm font-light leading-relaxed text-[#5A504B]">{first.excerpt}</p>
                <span className="mt-7 inline-flex items-center gap-2 text-sm font-medium text-[#1A1A1A]">
                  <span className="border-b border-[#D4AF37]/60 pb-0.5">Read the essay</span>
                  <ArrowUpRight className="h-4 w-4 text-[#B38B2D] transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" aria-hidden="true" />
                </span>
              </div>
            </Link>
          </Reveal>
        )}

        <div className="mt-16 grid gap-10 md:grid-cols-2 lg:grid-cols-3" data-testid="blog-grid">
          {rest.map((a, i) => (
            <Reveal key={a.id} delay={(i % 3) * 0.08}>
              <Link to={`/blog/${a.id}`} data-testid={`blog-card-${a.id}`} className="group block">
                <ZoomParallax src={a.image} alt={a.title} className="aspect-[4/3] rounded-[4px]" />
                <div className="mt-4 flex items-center gap-3 text-[10px] font-medium uppercase tracking-[0.2em] text-[#B38B2D]">
                  <span>{a.category}</span>
                  <span className="text-[#1A1A1A]/40">{a.read_time}</span>
                </div>
                <h3 className="mt-2 font-display text-2xl font-normal leading-snug text-[#1A1A1A] transition-colors group-hover:text-[#B38B2D]">{a.title}</h3>
                <p className="mt-2 text-sm font-light leading-relaxed text-[#5A504B]">{a.excerpt}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>

      <div className="mt-20">
        <VelocityMarquee items={["New Essays Weekly", "Skin Science", "Hair Restoration", "Wellness"]} />
      </div>

      <Reveal className="mx-auto mt-20 max-w-4xl">
        <div className="border border-[#1A1A1A]/10 p-10 text-center sm:p-14" data-testid="journal-cta">
          <p className="eyebrow">Every Week</p>
          <h2 className="mt-4 font-display text-4xl font-normal text-[#1A1A1A] sm:text-5xl">A new essay, every Friday</h2>
          <p className="mx-auto mt-4 max-w-md text-sm font-light text-[#5A504B]">
            Fresh guidance from Dr. Javeria's desk — or skip the reading and get a plan made for your skin.
          </p>
          <button onClick={() => book()} data-testid="journal-book-button" className="btn-gold mt-8 px-8 py-3.5 text-sm">
            Book Your Consultation
          </button>
        </div>
      </Reveal>
    </div>
  );
}
